import { Injectable } from '@angular/core';

interface ScriptConfig {
  src: string;
  id: string;
}

@Injectable({
  providedIn: 'root',
})
export class ScriptLoaderService {
  private loadedScripts = new Map<string, boolean>();
  private loadingPromises = new Map<string, Promise<void>>();

  constructor() {}

  /**
   * 加载天地图相关脚本
   */
  loadTianditu(): Promise<void> {
    const scripts: ScriptConfig[] = [
      {
        src: 'https://api.tianditu.gov.cn/api?v=4.0&tk=75883b41584f73af6de86793ae6b0aaa',
        id: 'tianditu-api',
      },
      {
        src: './assets/tianditu/d3.min.js',
        id: 'tianditu-d3',
      },
      {
        src: './assets/tianditu/D3SvgOverlay.js',
        id: 'tianditu-overlay',
      },
      {
        src: './assets/tianditu/CarTrack.js',
        id: 'tianditu-track',
      },
    ];

    return this.loadScriptGroup('tianditu', scripts);
  }

  /**
   * 加载微信登录脚本
   */
  loadWxLogin(): Promise<void> {
    const scripts: ScriptConfig[] = [
      {
        src: 'https://res.wx.qq.com/connect/zh_CN/htmledition/js/wxLogin.js',
        id: 'wx-login',
      },
    ];

    return this.loadScriptGroup('wxlogin', scripts);
  }

  /**
   * 加载打印插件脚本
   */
  loadLodop(): Promise<void> {
    const scripts: ScriptConfig[] = [
      {
        src: 'https://localhost.lodop.net:8443/CLodopfuncs.js',
        id: 'lodop',
      },
    ];

    return this.loadScriptGroup('lodop', scripts);
  }

  /**
   * 加载脚本组
   */
  private loadScriptGroup(groupName: string, scripts: ScriptConfig[]): Promise<void> {
    // 如果已经加载完成，直接返回成功的 Promise
    if (this.loadedScripts.get(groupName)) {
      return Promise.resolve();
    }

    // 如果正在加载中，返回同一个 Promise
    const existingPromise = this.loadingPromises.get(groupName);
    if (existingPromise) {
      return existingPromise;
    }

    // 开始加载
    const loadingPromise = this.loadScriptsSequentially(scripts).then(() => {
      this.loadedScripts.set(groupName, true);
      this.loadingPromises.delete(groupName);
    });

    this.loadingPromises.set(groupName, loadingPromise);
    return loadingPromise;
  }

  /**
   * 按顺序加载脚本（保证依赖关系）
   */
  private loadScriptsSequentially(scripts: ScriptConfig[]): Promise<void> {
    return scripts.reduce((promise, script) => {
      return promise.then(() => this.loadScript(script.src, script.id));
    }, Promise.resolve());
  }

  /**
   * 加载单个脚本
   */
  private loadScript(src: string, id: string): Promise<void> {
    return new Promise((resolve, reject) => {
      // 检查脚本是否已存在
      if (document.getElementById(id)) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.id = id;
      script.src = src;
      script.async = false; // 确保按顺序执行

      script.onload = () => {
        resolve();
      };

      script.onerror = () => {
        reject(new Error(`Failed to load script: ${src}`));
      };

      document.body.appendChild(script);
    });
  }

  /**
   * 检查天地图 API 是否已加载
   */
  isTiandituLoaded(): boolean {
    return this.loadedScripts.get('tianditu') && typeof (window as any).T !== 'undefined';
  }

  /**
   * 检查微信登录 API 是否已加载
   */
  isWxLoginLoaded(): boolean {
    return this.loadedScripts.get('wxlogin') && typeof (window as any).WxLogin !== 'undefined';
  }

  /**
   * 检查打印插件是否已加载
   */
  isLodopLoaded(): boolean {
    return (
      this.loadedScripts.get('lodop') &&
      (typeof (window as any).LODOP !== 'undefined' ||
        typeof (window as any).getCLodop !== 'undefined')
    );
  }
}
